import { COUNTRY_LABELS } from "@shared-kernel/domain/types/supported-country";
import { formatActivityRegistrationNumber, formatPhoneNumber, formatSiret } from "@utils/formatting";
import { formatLegalRepresentativeName } from "@academy-context/shared/domain/services/contract-signatory";
import { VariantCard } from "./variant-card.content";
import type { ProviderDisplayData } from "./types";

interface ProviderDisplayCardProps {
  provider: ProviderDisplayData;
  variant: "admin" | "provider";
}

const DEFAULT_TITLES = {
  admin: "Prestataire externe",
  provider: "Informations prestataire",
} as const;

/**
 * Shared component to display provider information in a grid layout.
 * Used by both admin and provider portal views.
 */
export const ProviderDisplayCard = ({ provider, variant }: ProviderDisplayCardProps) => {
  const title = DEFAULT_TITLES[variant];

  return (
    <VariantCard title={title} variant={variant}>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Nom:</span> {provider.name}
        </div>
        <div>
          <span className="text-muted-foreground">Contact:</span> {provider.contact.firstName} {provider.contact.lastName}
        </div>
        <div>
          <span className="text-muted-foreground">Représentant légal:</span> {formatLegalRepresentativeName(provider.legalRepresentative)}
        </div>
        <div>
          <span className="text-muted-foreground">Email:</span>{" "}
          <a href={`mailto:${provider.email}`} className="text-blue-600 hover:underline dark:text-blue-400">
            {provider.email}
          </a>
        </div>
        <div>
          <span className="text-muted-foreground">Téléphone:</span>{" "}
          {provider.phone ? (
            <a href={`tel:${provider.phone}`} className="text-blue-600 hover:underline dark:text-blue-400">
              {formatPhoneNumber(provider.phone)}
            </a>
          ) : (
            "Non renseigné"
          )}
        </div>
        <div>
          <span className="text-muted-foreground">SIRET:</span>{" "}
          {provider.siret ? (
            <span className="inline-flex items-center gap-1.5">
              <code className="text-xs">{formatSiret(provider.siret)}</code>
              {provider.country && <span title={COUNTRY_LABELS[provider.country].label}>{COUNTRY_LABELS[provider.country].flag}</span>}
            </span>
          ) : (
            "Non renseigné"
          )}
        </div>
        <div>
          <span className="text-muted-foreground">NDA:</span>{" "}
          {provider.activityRegistrationNumber ? (
            <code className="text-xs">{formatActivityRegistrationNumber(provider.activityRegistrationNumber)}</code>
          ) : (
            "Non renseigné"
          )}
        </div>
      </div>
    </VariantCard>
  );
};
